import React from 'react';

/**
 * Reusable data table component with pagination.
 *
 * Props:
 * - columns: Array of { key, label, render? }
 * - data: Array of row objects
 * - loading: boolean
 * - pagination: { page, limit, total }
 * - onPageChange: (page) => void
 * - actions?: (row) => React.Node
 * - emptyMessage?: string
 * - selectable?: boolean - Enable row selection with checkboxes
 * - selectedIds?: Set - Set of selected row IDs
 * - onSelectionChange?: (selectedIds: Set) => void - Callback when selection changes
 * - idKey?: string - Key to use as unique identifier (default: 'id')
 * - renderExpandedRow?: (row) => React.Node - Render expanded content below row
 */
function DataTable({
  columns,
  data,
  loading = false,
  pagination,
  onPageChange,
  actions,
  emptyMessage = 'No data available',
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  idKey = 'id',
  renderExpandedRow,
}) {
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.limit) : 1;

  const allSelected = selectable && data.length > 0 && data.every(row => selectedIds.has(row[idKey]));
  const someSelected = selectable && data.some(row => selectedIds.has(row[idKey]));

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      // Deselect all on current page
      const newSelected = new Set(selectedIds);
      data.forEach(row => newSelected.delete(row[idKey]));
      onSelectionChange(newSelected);
    } else {
      // Select all on current page
      const newSelected = new Set(selectedIds);
      data.forEach(row => newSelected.add(row[idKey]));
      onSelectionChange(newSelected);
    }
  };

  const handleSelectRow = (rowId) => {
    if (!onSelectionChange) return;
    const newSelected = new Set(selectedIds);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    onSelectionChange(newSelected);
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-8">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-900/50 border-b border-gray-700">
              {selectable && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-sm font-medium text-gray-400"
                >
                  {col.label}
                </th>
              ))}
              {actions && (
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, idx) => {
                const rowKey = row[idKey] || row.id || row.questionId || row.userId || row.testCaseId || idx;
                const expandedContent = renderExpandedRow ? renderExpandedRow(row) : null;
                return (
                  <React.Fragment key={rowKey}>
                    <tr
                      className={`border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors ${
                        selectable && selectedIds.has(row[idKey]) ? 'bg-blue-900/20' : ''
                      }`}
                    >
                      {selectable && (
                        <td className="px-4 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(row[idKey])}
                            onChange={() => handleSelectRow(row[idKey])}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
                          />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td key={col.key} className="px-4 py-3 text-sm text-gray-300">
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      ))}
                      {actions && (
                        <td className="px-4 py-3 text-right">
                          {actions(row)}
                        </td>
                      )}
                    </tr>
                    {expandedContent && (
                      <tr>
                        <td colSpan={columns.length + (actions ? 1 : 0) + (selectable ? 1 : 0)}>
                          {expandedContent}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
          <div className="text-sm text-gray-400">
            Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
            {pagination.total} results
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-1 text-gray-400">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
